import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Badge from 'react-bootstrap/Badge';
import Loader from '../../component/loader';
import { FiChevronLeft as ChevronLeft, FiChevronRight as ChevronRight } from "react-icons/fi";
import { TbTruckReturn } from "react-icons/tb";
import { AiOutlineClockCircle, AiOutlineCheckCircle, AiOutlineCloseCircle } from "react-icons/ai";
import { BsBoxSeam } from "react-icons/bs";
import axios from 'axios';
import Cookies from "js-cookie";
import { decrpt } from "../../component/encryption";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return String(dateStr);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  } catch (e) {
    return String(dateStr);
  }
};

function ReturnOrderList() {
  const [loader, setLoader] = useState(false);
  const [returnList, setReturnList] = useState([]);
  const [counts, setCounts] = useState({
    all: 0,
    requested: 0,
    approved: 0,
    picked_up: 0,
    refunded: 0,
    denied: 0
  });
  const [selectedStatus, setSelectedStatus] = useState(undefined);

  // Pagination & Search states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchText, setSearchText] = useState("");
  const navigate = useNavigate();

  const fetchReturnList = async (status) => {
    setLoader(true);
    try {
      const token = decrpt(Cookies.get('s_l'));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const params = {};
      if (status !== undefined) {
        params.status = status;
      }

      const response = await axios.post(`/seller/return/orders/list`, params);
      if (response.data && response.data.data) {
        setReturnList(Array.isArray(response.data.data) ? response.data.data : (response.data.data.list || []));
      } else {
        setReturnList([]);
      }
      if (response.data && response.data.counts) {
        setCounts(response.data.counts);
      }
      setLoader(false);
    } catch (err) {
      console.log('Return list error', err);
      setReturnList([]);
      setLoader(false);
    }
  };

  const handleStatusFilter = (status) => {
    setSelectedStatus(status);
    setCurrentPage(1);
    fetchReturnList(status);
  };

  // Filtered return orders by search
  const filteredList = useMemo(() => {
    if (!Array.isArray(returnList)) return [];
    if (!searchText.trim()) return returnList;
    const q = searchText.trim().toLowerCase();
    return returnList.filter((item) => {
      const orderUuid = (item.order?.order_uuid || "").toLowerCase();
      const userName = (item.user?.name || "").toLowerCase();
      const userMobile = (item.user?.mobile || "").toLowerCase();
      const prodName = (item.product?.name || "").toLowerCase();
      const reason = (item.return_reason || item.reason?.reason || "").toLowerCase();
      const id = String(item.id || "").toLowerCase();
      return (
        orderUuid.includes(q) ||
        userName.includes(q) ||
        userMobile.includes(q) ||
        prodName.includes(q) ||
        reason.includes(q) ||
        id.includes(q)
      );
    });
  }, [returnList, searchText]);

  // Pagination logic
  const totalItems = filteredList.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const paginatedList = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredList.slice(startIndex, endIndex);
  }, [filteredList, currentPage, itemsPerPage]);

  const calculatePaidAmount = (item) => {
    const base = Number(item.amount || 0);
    const gst = Number(item.product?.gst || 0);
    if (gst > 0 && Math.abs(base - Number(item.product?.price || 0)) < 0.05) {
      return Number((base + (base * gst / 100)).toFixed(2));
    }
    return base;
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const viewDetails = (item) => {
    navigate('/order/returnSingle', { state: { item: item.id } });
  };

  const getStatusBadge = (status) => {
    switch (String(status)) {
      case '1':
        return <Badge bg="warning" className="text-dark">Requested</Badge>;
      case '2':
        return <Badge bg="info" className="text-white">Approved</Badge>;
      case '3':
        return <Badge bg="primary">Picked Up</Badge>;
      case '4':
        return <Badge bg="success">Refunded</Badge>;
      case '5':
        return <Badge bg="danger">Denied</Badge>;
      default:
        return <Badge bg="secondary">Pending</Badge>;
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchReturnList(selectedStatus);
  }, []);

  return (
    <div className="main-content">
      {loader ? (
        <Loader />
      ) : (
        <>
          <ToastContainer position="top-center" autoClose={3000} hideProgressBar={false} theme={'colored'} />
          <div className="section returnOrderList">
            <div className="row">
              <div className="col-md-12">
                <div className="section-title">
                  <h5><TbTruckReturn className="me-1" /> Returned Orders Management</h5>
                </div>

                {/* Filter Tabs */}
                <div className="count-section d-flex justify-content-left flex-wrap gap-2 mb-3 mt-2">
                  <button
                    className="product-page-block d-flex align-items-center me-2"
                    onClick={() => handleStatusFilter(undefined)}
                    style={selectedStatus === undefined ? styles.activeBtn : null}
                  >
                    <div className="product-page-inner-block">
                      <h5 style={selectedStatus === undefined ? styles.activeBtnTxt : null}>
                        <TbTruckReturn /> All ({counts.all})
                      </h5>
                    </div>
                  </button>

                  <button
                    className="product-page-block d-flex align-items-center me-2"
                    onClick={() => handleStatusFilter(1)}
                    style={selectedStatus === 1 ? styles.activeBtn : null}
                  >
                    <div className="product-page-inner-block">
                      <h5 style={selectedStatus === 1 ? styles.activeBtnTxt : null}>
                        <AiOutlineClockCircle /> Requested ({counts.requested})
                      </h5>
                    </div>
                  </button>

                  <button
                    className="product-page-block d-flex align-items-center me-2"
                    onClick={() => handleStatusFilter(2)}
                    style={selectedStatus === 2 ? styles.activeBtn : null}
                  >
                    <div className="product-page-inner-block">
                      <h5 style={selectedStatus === 2 ? styles.activeBtnTxt : null}>
                        <AiOutlineCheckCircle /> Approved ({counts.approved})
                      </h5>
                    </div>
                  </button>

                  <button
                    className="product-page-block d-flex align-items-center me-2"
                    onClick={() => handleStatusFilter(3)}
                    style={selectedStatus === 3 ? styles.activeBtn : null}
                  >
                    <div className="product-page-inner-block">
                      <h5 style={selectedStatus === 3 ? styles.activeBtnTxt : null}>
                        <BsBoxSeam /> Picked Up ({counts.picked_up})
                      </h5>
                    </div>
                  </button>

                  <button
                    className="product-page-block d-flex align-items-center me-2"
                    onClick={() => handleStatusFilter(4)}
                    style={selectedStatus === 4 ? styles.activeBtn : null}
                  >
                    <div className="product-page-inner-block">
                      <h5 style={selectedStatus === 4 ? styles.activeBtnTxt : null}>
                        <AiOutlineCheckCircle /> Refunded ({counts.refunded})
                      </h5>
                    </div>
                  </button>

                  <button
                    className="product-page-block d-flex align-items-center"
                    onClick={() => handleStatusFilter(5)}
                    style={selectedStatus === 5 ? styles.activeBtn : null}
                  >
                    <div className="product-page-inner-block">
                      <h5 style={selectedStatus === 5 ? styles.activeBtnTxt : null}>
                        <AiOutlineCloseCircle /> Denied ({counts.denied})
                      </h5>
                    </div>
                  </button>
                </div>

                {/* Table Card */}
                <div className="card p-2 mt-3">
                  <div className="card-title d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <h5>You have {totalItems} Return Orders</h5>
                    <div style={{ maxWidth: '320px', width: '100%' }}>
                      <input
                        type="text"
                        placeholder="Search by Order ID, Customer, Product..."
                        value={searchText}
                        onChange={(e) => {
                          setSearchText(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="form-control form-control-sm"
                        style={{ border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem' }}
                      />
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-bordered align-middle">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: "50px" }}>SL</th>
                          <th>Order ID</th>
                          <th>Customer</th>
                          <th>Product</th>
                          <th>Amount</th>
                          <th>Return Reason</th>
                          <th className="text-center">Stage</th>
                          <th className="text-center">Requested On</th>
                          <th className="text-center" style={{ width: "120px" }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedList && paginatedList.length > 0 ? (
                          paginatedList.map((item, index) => {
                            const sl = ((currentPage - 1) * itemsPerPage) + index + 1;
                            return (
                              <tr key={item.id || index}>
                                <td>{sl}.</td>
                                <td>
                                  <strong>{item.order?.order_uuid || `#${item.order_id}`}</strong>
                                </td>
                                <td>
                                  <div><strong>{item.user?.name || "N/A"}</strong></div>
                                  <small className="text-muted">{item.user?.mobile}</small>
                                </td>
                                <td>
                                  <div>
                                    <div className="fw-semibold text-truncate" style={{ maxWidth: '240px' }} title={item.product?.name || "Product"}>
                                      {item.product?.name || "Product"}
                                    </div>
                                    {item.product_size?.name && (
                                      <small className="text-muted d-block">Size: {item.product_size.name}</small>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <strong>₹{calculatePaidAmount(item).toFixed(2)}</strong>
                                </td>
                                <td>
                                  <span className="text-truncate d-inline-block" style={{ maxWidth: '180px' }} title={item.return_reason || item.reason?.reason || "N/A"}>
                                    {item.return_reason || item.reason?.reason || "N/A"}
                                  </span>
                                </td>
                                <td className="text-center">
                                  {getStatusBadge(item.status)}
                                </td>
                                <td className="text-center text-muted small">
                                  {formatDate(item.created_at)}
                                </td>
                                <td className="text-center">
                                  <button
                                    type="button"
                                    onClick={() => viewDetails(item)}
                                    className="btn btn-sm btn-primary"
                                    style={{ fontSize: "12px", padding: "4px 10px" }}
                                  >
                                    View Details
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="9" className="text-center py-4 text-muted">
                              No return orders found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    {/* Pagination Controls */}
                    {totalItems > 0 && (
                      <div
                        className="d-flex items-center justify-between border-t border-gray-200 bg-white px-3 py-2 flex-wrap gap-2"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          borderTop: '1px solid #e5e7eb',
                          padding: '0.75rem 1rem',
                          background: '#fff'
                        }}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-sm text-gray-700" style={{ fontSize: '0.875rem', color: '#374151' }}>
                            Items per page:
                          </span>
                          <select
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            className="form-select form-select-sm"
                            style={{
                              width: 'auto',
                              display: 'inline-block',
                              fontSize: '0.875rem',
                              padding: '0.25rem 1.5rem 0.25rem 0.5rem'
                            }}
                          >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              padding: '0.25rem 0.75rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              background: '#fff',
                              color: '#000',
                              opacity: currentPage === 1 ? 0.5 : 1,
                              cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                            }}
                          >
                            <ChevronLeft style={{ height: '1rem', width: '1rem' }} /> Previous
                          </button>

                          <div className="d-flex align-items-center gap-1">
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                              .filter((page) => {
                                return (
                                  page === 1 ||
                                  page === totalPages ||
                                  Math.abs(page - currentPage) <= 1
                                );
                              })
                              .map((page, idx, arr) => {
                                const showEllipsisBefore = idx > 0 && page - arr[idx - 1] > 1;
                                return (
                                  <React.Fragment key={page}>
                                    {showEllipsisBefore && (
                                      <span className="px-1 text-muted" style={{ padding: '0 0.25rem' }}>...</span>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => handlePageChange(page)}
                                      style={{
                                        padding: '0.25rem 0.6rem',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '0.375rem',
                                        fontSize: '0.875rem',
                                        fontWeight: 500,
                                        background: currentPage === page ? '#ffc001' : '#fff',
                                        color: '#000',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      {page}
                                    </button>
                                  </React.Fragment>
                                );
                              })}
                          </div>

                          <button
                            type="button"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              padding: '0.25rem 0.75rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              background: '#fff',
                              color: '#000',
                              opacity: currentPage === totalPages ? 0.5 : 1,
                              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                            }}
                          >
                            Next <ChevronRight style={{ height: '1rem', width: '1rem' }} />
                          </button>
                        </div>

                        <div style={{ fontSize: '0.875rem', color: '#374151' }}>
                          Showing {totalItems === 0 ? 0 : ((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ReturnOrderList;

const styles = {
  activeBtn: { backgroundColor: '#ffc001ad' },
  activeBtnTxt: { color: '#000' },
};

